import { useQueryClient } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import { useAuthenticatedQuery } from "@/hooks/use-api";
import type { Quote } from "@onsemble/shared";
import type { InsertQuote } from "@shared/schema";
import {
  useCreateQuote,
  type CreateQuoteInput,
} from "@/requests/quotes/create-quote";
import { calculateAmountPreDiscount } from "@/components/quotes/PricingCalculator";
import type { QuoteService } from "@onsemble/shared";

// Extend the base InsertQuote for frontend-specific fields
interface CreateQuoteData extends Omit<InsertQuote, "orgId"> {
  // quoteServices and quoteCustomParts already included in InsertQuote - no transformation needed
}

interface UseQuotesResult {
  quotes: Quote[];
  isLoading: boolean;
  error: unknown;
  createQuote: (data: CreateQuoteData) => Promise<any>;
  isCreating: boolean;
}

export const useQuotes = (): UseQuotesResult => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useAuthenticatedQuery(
    ["/api/quotes", organization?.id],
    {
      enabled: !!user && !!organization,
    },
  );

  // Use the new typed create quote hook
  const createQuoteMutation = useCreateQuote();

  const createQuote = async (data: CreateQuoteData): Promise<Quote> => {
    if (!organization?.id) {
      throw new Error("Organization data not available");
    }

    // Ensure amount is a valid number in cents
    const amount =
      typeof data.amountPresalesTax === "string"
        ? parseFloat(data.amountPresalesTax)
        : data.amountPresalesTax;

    if (isNaN(amount) || amount < 0) {
      throw new Error(`Invalid quote amount: ${data.amountPresalesTax}`);
    }

    if (data.taxAmount == null) {
      throw new Error("Tax amount is required for quote creation");
    }

    const taxAmountCents = data.taxAmount;
    const amountPostTax = amount + taxAmountCents;

    // Convert numeric values
    const laborHours =
      typeof data.laborHours === "string"
        ? parseFloat(data.laborHours)
        : data.laborHours;

    const laborRate = data.laborRate || 0;

    const markupPercent =
      typeof data.markupPercent === "string"
        ? parseFloat(data.markupPercent)
        : data.markupPercent;

    const discountPercentage =
      typeof data.discountPercentage === "string"
        ? parseFloat(data.discountPercentage)
        : data.discountPercentage;

    // Extract service data from quoteServices for pricing calculation
    const quoteServices = (data.quoteServices || []) as QuoteService[];
    const selectedServices = quoteServices
      .map((qs) => qs.id)
      .filter(Boolean) as string[];
    const serviceCustomPrices: Record<string, number> = {};

    quoteServices.forEach((qs) => {
      if (qs.id && qs.price !== undefined && qs.price !== null) {
        serviceCustomPrices[qs.id] = qs.price;
      }
    });

    // Create service objects for pricing calculation
    const serviceObjects = selectedServices.map((serviceId) => ({
      uid: serviceId,
      cost: serviceCustomPrices[serviceId] || 0,
      // Other fields not needed for calculation
      title: "",
      orgId: "",
      description: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    // Calculate amount_prediscount using the pricing calculator
    const amountPrediscount = calculateAmountPreDiscount({
      rate: laborRate,
      laborHours: laborHours,
      partsCostCents: data.partsCostPremarkup,
      markupPercent: markupPercent,
      partsAffectPrice: data.partsAffectPrice,
      services: serviceObjects,
      selectedServices: selectedServices,
      serviceCustomPrices: serviceCustomPrices,
    });

    // Prepare request data matching OpenAPI schema
    const requestData: CreateQuoteInput = {
      title: data.title,
      description: data.description,
      notes: data.notes ?? undefined,
      customerId: data.customerId,
      amountPrediscount: amountPrediscount,
      amountPresalesTax: amount,
      amountPostsalesTax: amountPostTax,
      taxAmount: taxAmountCents,
      laborHours: laborHours ?? undefined,
      laborRate: laborRate ?? undefined,
      markupPercent: markupPercent ?? undefined,
      partsCostPremarkup: data.partsCostPremarkup || 0,
      partsCostPostmarkup: data.partsCostPostmarkup || 0,
      partsAffectPrice: data.partsAffectPrice ?? true,
      discountPercentage: discountPercentage ?? undefined,
      discountDescription: data.discountDescription ?? undefined,
      quoteServices: data.quoteServices || [],
      quoteCustomParts: data.quoteCustomParts || [],
      products: data.products || [],
      quoteStatus: data.quoteStatus || "ready_to_send",
    };

    try {
      const newQuote = await createQuoteMutation.mutateAsync(requestData);
      queryClient.invalidateQueries({
        queryKey: ["/api/quotes", organization?.id],
      });
      toast({
        title: "Quote created successfully",
      });
      return newQuote;
    } catch (error) {
      console.error("Error creating quote:", error);
      toastApiError(toast, error, "Failed to create quote");
      throw error;
    }
  };

  return {
    quotes: (data as Quote[]) || [],
    isLoading,
    error,
    createQuote,
    isCreating: createQuoteMutation.isPending,
  };
};

export default useQuotes;
